import { useCallback, useState } from "react";
import type { RequestStep } from "./RequestModalShell";

/**
 * Which step of a request modal is showing, and where the user may go. A step
 * can be opened once every step before it is complete, so the stepper never
 * lets anyone skip a required field. Replaces `useWizardTabs`, whose guards
 * every modal had to restate by hand.
 */
export function useRequestSteps(
  steps: RequestStep[],
  isStepComplete: Record<string, boolean>
) {
  const firstStepId = steps[0]?.id ?? "";
  const [requestedStepId, setRequestedStepId] = useState(firstStepId);

  // The steps can change under the modal (a product switch drops Kontrol
  // Edge's service step); fall back to the first instead of showing nothing.
  const activeStepId = steps.some((s) => s.id === requestedStepId)
    ? requestedStepId
    : firstStepId;

  const canActivate = useCallback(
    (stepId: string) => {
      const index = steps.findIndex((s) => s.id === stepId);
      if (index < 0) return false;
      return steps
        .slice(0, index)
        .every((s) => isStepComplete[s.id] ?? true);
    },
    [steps, isStepComplete]
  );

  const goToStep = useCallback(
    (stepId: string) => {
      if (canActivate(stepId)) setRequestedStepId(stepId);
    },
    [canActivate]
  );

  const resetSteps = useCallback(
    () => setRequestedStepId(firstStepId),
    [firstStepId]
  );

  return {
    activeStepId,
    canActivate,
    goToStep,
    resetSteps,
    canContinue: isStepComplete[activeStepId] ?? true,
  };
}
